import React, { useContext, useEffect, useState } from "react";
import { v4 as uuidV4 } from "uuid";
import { Alert, Container, Row, Col, Form, Button, ProgressBar } from "react-bootstrap";
import InputMask from "react-input-mask";
import { BsPersonPlusFill } from "react-icons/bs";

import { BoletimContext } from "../../../../context/BoletimContext";
import api from "../../../../Utils/api";
import {
  dataParaInput,
  dataParaPersistencia,
  formatarTelefone,
  isValidCpf,
  normalizarEnvolvido,
  opcoesCorRaca,
  opcoesEscolaridade,
  opcoesEstadoCivil,
  opcoesSexo,
  opcoesUf,
  validarEnvolvido,
} from "./envolvidoUtils";
import "../styles/form-envolvido.css";

const tiposEnvolvimento = [
  "Autor", "Suspeito", "Condutor", "Vítima", "Testemunha", "Comunicante", "Vítima Fatal", "Outro",
];

function Campo({ id, label, value, onChange, erro, type = "text", ...props }) {
  return (
    <Form.Group className="bo-field" controlId={id}>
      <Form.Label>{label}</Form.Label>
      <Form.Control
        type={type}
        value={value ?? ""}
        onChange={(event) => onChange(event.target.value)}
        isInvalid={Boolean(erro)}
        {...props}
      />
      {erro && <Form.Control.Feedback type="invalid">{erro}</Form.Control.Feedback>}
    </Form.Group>
  );
}

function Selecao({ id, label, value, onChange, options, erro, ...props }) {
  return (
    <Form.Group className="bo-field" controlId={id}>
      <Form.Label>{label}</Form.Label>
      <Form.Select
        value={value ?? ""}
        onChange={(event) => onChange(event.target.value)}
        isInvalid={Boolean(erro)}
        {...props}
      >
        <option value="">Selecione</option>
        {options.map((option) => {
          const optionValue = Array.isArray(option) ? option[0] : option;
          const optionLabel = Array.isArray(option) ? option[1] : option;
          return <option key={optionValue} value={optionValue}>{optionLabel}</option>;
        })}
      </Form.Select>
      {erro && <Form.Control.Feedback type="invalid">{erro}</Form.Control.Feedback>}
    </Form.Group>
  );
}

function FormEnvolvido({ envolvido, setEnvolvido, modoEdicao, setModoEdicao }) {
  const { setBoletim } = useContext(BoletimContext);
  const [consultandoCpf, setConsultandoCpf] = useState(false);
  const [resultadoCpf, setResultadoCpf] = useState(null);
  const [erros, setErros] = useState({});
  const hoje = new Date();
  const dataMaxima = `${hoje.getFullYear()}-${String(hoje.getMonth() + 1).padStart(2, "0")}-${String(hoje.getDate()).padStart(2, "0")}`;

  useEffect(() => {
    setBoletim((current) => ({
      ...current,
      formDrafts: { ...current.formDrafts, envolvido },
    }));
  }, [envolvido, setBoletim]);

  const atualizar = (campo, valor) => {
    setEnvolvido((current) => ({ ...current, [campo]: valor }));
    setErros((current) => ({ ...current, [campo]: undefined }));
  };

  const novoCadastro = () => {
    setEnvolvido(normalizarEnvolvido());
    setErros({});
    setResultadoCpf(null);
  };

  const cancelarEdicao = () => {
    novoCadastro();
    setModoEdicao(false);
    setBoletim((current) => ({
      ...current,
      formDrafts: { ...current.formDrafts, envolvido: null },
    }));
  };

  const consultarCpf = async () => {
    const cpfDigits = String(envolvido.cpf || "").replace(/\D/g, "");
    if (!isValidCpf(cpfDigits)) {
      setResultadoCpf({
        tipo: "warning",
        mensagem: "Informe um CPF válido para consultar ou deixe o campo em branco.",
      });
      return;
    }
    setConsultandoCpf(true);
    setResultadoCpf(null);
    try {
      const { data } = await api.get(`/incidentReport/involved/cpf/${cpfDigits}`);
      if (!data.found) {
        setResultadoCpf({
          tipo: "info",
          mensagem: "CPF não encontrado nos seus boletins. Preencha os dados do envolvido.",
        });
        return;
      }
      setEnvolvido((current) => ({
        ...normalizarEnvolvido({ ...current, ...data.pessoa }),
        conduzido: Boolean(current.conduzido),
      }));
      setResultadoCpf({
        tipo: "success",
        mensagem: "Dados encontrados e preenchidos. Você pode editá-los antes de salvar.",
      });
    } catch (error) {
      setResultadoCpf({
        tipo: "danger",
        mensagem: error.response?.data?.message || "Não foi possível consultar o CPF.",
      });
    } finally {
      setConsultandoCpf(false);
    }
  };

  const submitForm = (event) => {
    event.preventDefault();
    const validacao = validarEnvolvido(envolvido);
    if (validacao) {
      setErros({ [validacao.campo]: validacao.mensagem });
      document.getElementById(`envolvido-${validacao.campo}`)?.focus();
      return;
    }

    const registro = {
      ...envolvido,
      id: envolvido.id || uuidV4(),
      nascimento: dataParaPersistencia(envolvido.nascimento),
      uf: String(envolvido.uf || "").trim(),
    };
    setBoletim((current) => {
      const envolvidos = [...(current.envolvidos || [])];
      if (modoEdicao) {
        const indice = envolvidos.findIndex((item) =>
          (envolvido.id && item.id === envolvido.id) ||
          (envolvido._id && String(item._id) === String(envolvido._id))
        );
        if (indice >= 0) {
          envolvidos[indice] = { ...envolvidos[indice], ...registro };
        } else {
          envolvidos.push(registro);
        }
      } else {
        envolvidos.push(registro);
      }
      return {
        ...current,
        envolvidos,
        formDrafts: { ...current.formDrafts, envolvido: null },
      };
    });
    setModoEdicao(false);
    novoCadastro();
  };

  const erro = (campo) => erros[campo];
  const ufSelecionado = opcoesUf.some(([value]) => value === envolvido.uf)
    ? envolvido.uf
    : envolvido.uf ? "OUTRO" : "";

  return (
    <Container fluid className="bo-form-root">
      <ProgressBar now={40} className="bo-progress" />
      <Form onSubmit={submitForm} noValidate>
        <section className="bo-section">
          <h5 className="bo-section-title">Documentos</h5>
          <p className="text-muted">
            Informe o CPF e consulte os boletins acessíveis à sua conta para preencher dados já cadastrados.
          </p>
          <Row className="g-2">
            <Col xs={12} md={6}>
              <Form.Group className="bo-field" controlId="envolvido-cpf">
                <Form.Label>CPF (opcional)</Form.Label>
                <div className="d-flex flex-column flex-sm-row gap-2">
                  <InputMask
                    mask="999.999.999-99"
                    maskChar={null}
                    placeholder="000.000.000-00"
                    value={envolvido.cpf || ""}
                    onChange={(event) => {
                      setResultadoCpf(null);
                      atualizar("cpf", event.target.value);
                    }}
                  >
                    {(inputProps) => (
                      <Form.Control
                        {...inputProps}
                        type="text"
                        inputMode="numeric"
                        autoComplete="off"
                        autoFocus
                        isInvalid={Boolean(erro("cpf"))}
                      />
                    )}
                  </InputMask>
                  <Button
                    type="button"
                    variant="outline-primary"
                    onClick={consultarCpf}
                    disabled={consultandoCpf || !isValidCpf(envolvido.cpf)}
                  >
                    {consultandoCpf ? "Consultando..." : "Consultar CPF"}
                  </Button>
                </div>
                {erro("cpf") && <Form.Control.Feedback type="invalid" className="d-block">{erro("cpf")}</Form.Control.Feedback>}
                {resultadoCpf && (
                  <Alert variant={resultadoCpf.tipo} className="mt-2 mb-0" role="status">
                    {resultadoCpf.mensagem}
                  </Alert>
                )}
              </Form.Group>
            </Col>
            <Col xs={12} md={6}>
              <Campo
                id="envolvido-cnhPpd"
                label="CNH/PPD (opcional)"
                value={envolvido.cnhPpd}
                onChange={(value) => atualizar("cnhPpd", value)}
                inputMode="numeric"
                maxLength={20}
                autoComplete="off"
              />
            </Col>
          </Row>
        </section>

        <section className="bo-section">
          <h5 className="bo-section-title">Identificação pessoal</h5>
          <Row className="g-2">
            <Col xs={12} md={6}>
              <Selecao
                id="envolvido-tipo"
                label="Tipo de envolvimento"
                value={envolvido.tipo}
                options={tiposEnvolvimento}
                onChange={(value) => atualizar("tipo", value)}
              />
            </Col>
            <Col xs={12} md={6}>
              <Campo
                id="envolvido-nome"
                label="Nome completo"
                value={envolvido.nome}
                onChange={(value) => atualizar("nome", value)}
                erro={erro("nome")}
                required
                autoComplete="name"
              />
            </Col>
            <Col xs={12} md={6}>
              <Campo id="envolvido-nomeSocial" label="Nome social" value={envolvido.nomeSocial} onChange={(value) => atualizar("nomeSocial", value)} />
            </Col>
            <Col xs={12} md={6}>
              <Campo id="envolvido-alcunha" label="Alcunha" value={envolvido.alcunha} onChange={(value) => atualizar("alcunha", value)} />
            </Col>
            <Col xs={12} md={6}>
              <Form.Group className="bo-field" controlId="envolvido-nascimento">
                <Form.Label>Data de nascimento</Form.Label>
                <Form.Control
                  type="date"
                  value={dataParaInput(envolvido.nascimento)}
                  max={dataMaxima}
                  onChange={(event) => atualizar("nascimento", event.target.value)}
                  isInvalid={Boolean(erro("nascimento"))}
                />
                {erro("nascimento") && <Form.Control.Feedback type="invalid">{erro("nascimento")}</Form.Control.Feedback>}
              </Form.Group>
            </Col>
            <Col xs={12} md={6}>
              <Selecao id="envolvido-sexo" label="Sexo (conforme documento)" value={envolvido.sexo} options={opcoesSexo} onChange={(value) => atualizar("sexo", value)} />
            </Col>
          </Row>
        </section>

        <section className="bo-section">
          <h5 className="bo-section-title">Filiação</h5>
          <Row className="g-2">
            <Col xs={12} md={6}>
              <Campo id="envolvido-nomeMae" label="Nome da mãe" value={envolvido.nomeMae} onChange={(value) => atualizar("nomeMae", value)} />
            </Col>
            <Col xs={12} md={6}>
              <Campo id="envolvido-nomePai" label="Nome do pai" value={envolvido.nomePai} onChange={(value) => atualizar("nomePai", value)} />
            </Col>
          </Row>
        </section>

        <section className="bo-section">
          <h5 className="bo-section-title">Características e qualificação</h5>
          <Row className="g-2">
            <Col xs={12} md={6}>
              <Selecao id="envolvido-corRaca" label="Cor ou raça (autodeclaração)" value={envolvido.corRaca} options={opcoesCorRaca} onChange={(value) => atualizar("corRaca", value)} />
            </Col>
            <Col xs={12} md={6}>
              <Campo id="envolvido-orientacaoSexual" label="Orientação sexual (se informada)" value={envolvido.orientacaoSexual} onChange={(value) => atualizar("orientacaoSexual", value)} autoComplete="off" />
            </Col>
            <Col xs={12} md={6}>
              <Campo id="envolvido-naturalidade" label="Naturalidade (estado de nascimento)" value={envolvido.naturalidade} onChange={(value) => atualizar("naturalidade", value)} />
            </Col>
            <Col xs={12} md={6}>
              <Campo id="envolvido-profissao" label="Profissão" value={envolvido.profissao} onChange={(value) => atualizar("profissao", value)} />
            </Col>
            <Col xs={12} md={6}>
              <Selecao id="envolvido-estadoCivil" label="Estado civil" value={envolvido.estadoCivil} options={opcoesEstadoCivil} onChange={(value) => atualizar("estadoCivil", value)} />
            </Col>
            <Col xs={12} md={6}>
              <Selecao id="envolvido-escolaridade" label="Escolaridade" value={envolvido.escolaridade} options={opcoesEscolaridade} onChange={(value) => atualizar("escolaridade", value)} />
            </Col>
            <Col xs={12}>
              <Form.Check
                type="checkbox"
                id="envolvido-conduzido"
                label="Este envolvido foi conduzido à delegacia"
                checked={Boolean(envolvido.conduzido)}
                onChange={(event) => atualizar("conduzido", event.target.checked)}
              />
            </Col>
            <Col xs={12}>
              <Campo id="envolvido-obs" label="Observações" value={envolvido.obs} onChange={(value) => atualizar("obs", value)} />
            </Col>
          </Row>
        </section>

        <section className="bo-section">
          <h5 className="bo-section-title">Endereço residencial</h5>
          <Row className="g-2">
            <Col xs={12} md={8}>
              <Campo
                id="envolvido-endereco"
                label="Logradouro / endereço"
                value={envolvido.endereco}
                onChange={(value) => atualizar("endereco", value)}
                autoComplete="street-address"
              />
            </Col>
            <Col xs={12} md={4}>
              <Campo id="envolvido-numero" label="Número (se houver)" value={envolvido.numero} onChange={(value) => atualizar("numero", value)} />
            </Col>
            <Col xs={12} md={6}>
              <Campo id="envolvido-complemento" label="Complemento" value={envolvido.complemento} onChange={(value) => atualizar("complemento", value)} />
            </Col>
            <Col xs={12} md={6}>
              <Campo id="envolvido-bairro" label="Bairro" value={envolvido.bairro} onChange={(value) => atualizar("bairro", value)} autoComplete="address-level3" />
            </Col>
            <Col xs={12} md={6}>
              <Campo id="envolvido-municipio" label="Município" value={envolvido.municipio} onChange={(value) => atualizar("municipio", value)} autoComplete="address-level2" />
            </Col>
            <Col xs={12} md={6}>
              <Selecao
                id="envolvido-uf"
                label="Estado/UF ou província"
                value={ufSelecionado}
                options={[...opcoesUf, ["OUTRO", "Outro estado/província"]]}
                onChange={(value) => atualizar("uf", value === "OUTRO" ? " " : value)}
              />
              {ufSelecionado === "OUTRO" && (
                <Campo
                  id="envolvido-ufOutro"
                  label="Estado/província"
                  value={envolvido.uf === " " ? "" : envolvido.uf}
                  onChange={(value) => atualizar("uf", value)}
                />
              )}
            </Col>
            <Col xs={12}>
              <Campo id="envolvido-pontoReferencia" label="Ponto de referência" value={envolvido.pontoReferencia} onChange={(value) => atualizar("pontoReferencia", value)} />
            </Col>
          </Row>
        </section>

        <section className="bo-section">
          <h5 className="bo-section-title">Contato</h5>
          <Row className="g-2">
            <Col xs={12} md={6}>
              <Campo
                id="envolvido-telefone"
                label="Telefone (opcional)"
                value={envolvido.telefone}
                onChange={(value) => atualizar("telefone", formatarTelefone(value))}
                erro={erro("telefone")}
                type="tel"
                inputMode="tel"
                autoComplete="tel"
              />
            </Col>
          </Row>
        </section>

        <div className="d-flex flex-wrap justify-content-center gap-2 mt-3 mb-4">
          <Button type="submit" className="btn-warning">
            <BsPersonPlusFill size={20} className="me-2" />
            {modoEdicao ? "Salvar alterações" : "Adicionar envolvido"}
          </Button>
          {modoEdicao && (
            <Button type="button" variant="outline-secondary" onClick={cancelarEdicao}>
              Cancelar edição
            </Button>
          )}
        </div>
      </Form>
    </Container>
  );
}

export default FormEnvolvido;
